import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BUSINESS_NAME } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animation constants to prevent re-renders from inline objects
const FADE_IN = { opacity: 1 };
const FADE_OUT = { opacity: 0 };
const SLIDE_UP = { opacity: 1, y: 0 };
const SLIDE_DOWN_INITIAL = { opacity: 0, y: 20 };
const SCALE_IN = { scale: 1, opacity: 1 };
const SCALE_OUT = { scale: 0.95, opacity: 0 };

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, updateUser } = useAuth();
  
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  
  const verificationToken = searchParams.get('token');
  
  const verifyEmail = useCallback(async () => {
    if (!verificationToken) return;
    
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-email?token=${verificationToken}`);
      
      if (response.data.success) {
        setVerified(true);
        toast.success('Email verified successfully!');
        
        // Update user context if logged in
        if (user && updateUser) {
          updateUser({ ...user, email_verified: true });
        }
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  }, [verificationToken, user, updateUser, navigate]);
  
  useEffect(() => {
    if (verificationToken) {
      verifyEmail();
    }
  }, [verificationToken, verifyEmail]);
  
  const handleResendVerification = async () => {
    if (!token) {
      toast.error('Please log in to resend verification email');
      navigate('/login');
      return;
    }
    
    setResending(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Verification email sent! Check your inbox.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };
  
  // No token provided - show instructions
  if (!verificationToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
          </div>
          
          <div className="bg-card rounded-2xl border border-border/50 shadow-lg p-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
              <Mail className="w-10 h-10 text-amber-600" />
            </div>
            
            <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Verify Your Email
            </h1>
            
            <p className="text-muted-foreground mb-6">
              We've sent a verification email to your inbox. Click the link in the email to verify your account.
            </p>
            
            <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-semibold text-amber-800 mb-2">Didn't receive the email?</h3>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Check your spam/junk folder</li>
                <li>• Make sure you entered the correct email</li>
                <li>• Wait a few minutes and try again</li>
              </ul>
            </div>
            
            <Button
              onClick={handleResendVerification}
              disabled={resending}
              className="w-full rounded-xl mb-4"
              variant="outline"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Resend Verification Email
            </Button>
            
            <Button
              onClick={() => navigate('/login')}
              variant="ghost"
              className="w-full"
            >
              Back to Login
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Verifying state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
          <h2 className="text-xl font-semibold">Verifying your email...</h2>
          <p className="text-muted-foreground">Please wait a moment</p>
        </motion.div>
      </div>
    );
  }
  
  // Verified successfully
  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
          </div>
          
          <div className="bg-card rounded-2xl border border-border/50 shadow-lg p-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center"
            >
              <CheckCircle className="w-14 h-14 text-green-600" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-green-600 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Email Verified!
            </h1>
            
            <p className="text-muted-foreground mb-6">
              Your email has been verified successfully. You can now access all features.
            </p>
            
            <p className="text-sm text-muted-foreground mb-6">
              Redirecting to dashboard in 3 seconds...
            </p>
            
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full rounded-xl bg-primary"
            >
              Go to Dashboard Now
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-6">
          <Leaf className="w-10 h-10 text-primary" />
          <span className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{BUSINESS_NAME}</span>
        </div>
        
        <div className="bg-card rounded-2xl border border-border/50 shadow-lg p-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-red-600 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Verification Failed
          </h1>
          
          <p className="text-muted-foreground mb-6">
            {error || 'The verification link is invalid or has expired.'}
          </p>
          
          <Button
            onClick={handleResendVerification}
            disabled={resending}
            className="w-full rounded-xl mb-4"
          >
            {resending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Request New Verification Email
          </Button>
          
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="w-full"
          >
            Back to Login
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
